import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import useBranding from "../branding/useBranding";
import useTranslations from "../i18n/useTranslations";
import useUser from "../user/useUser";
import useMenu from "../../components/LoggedinPage/useMenu";

const routeTranslationMap: Record<string, string> = {
  // Alarm History
  alarms: "Menu:Alarm history",

  // Objects detail pages
  objects: "Menu:Objects",
  accessories: "Menu:Objects_Accessories",
  response: "Menu:Objects_Response",
  subscription: "Menu:Objects_Subscription",
  "alarm-history": "Menu:alarmhistory",
  "planning-overview": "Objects:planning_overview_title",
  "planning-history": "Objects:planning_history",
  accesslog: "Menu:Objects_AccessLog",

  // Reports detail pages
  reports: "Menu:Reports",

  // Administrators detail pages
  "menu-alternatives": "Administrator:menu_alternatives_title",
  permissions: "Menu:Administrators_Permissions",
  groups: "Administrator:groups_title",
  "admin-settings": "Administrator:settings_title",

  // Organizations detail pages
  organizations: "Menu:Organizations",
  "sub-organizations": "Menu:Organization_SubOrganizations",
  "ad-management": "Menu:Organization_ADManagement",
  administrators: "Menu:administrators",
  "retention-details": "Menu:Organization_RetentionDetails",
  "alarm-receptions": "Menu:alarmreceptions",
  integrations: "Menu:intergations_instances",
  "advanced-settings": "Menu:advanced_settings",

  // Alarm Receptions detail pages
  respondents: "Menu:AlarmReceptions_Respondents",
  "alarm-handling": "Menu:AlarmReceptions_AlarmHandling",
  "alarm-code-rules": "Menu:AlarmReceptions_AlarmCodeRules",
  distribution: "Menu:AlarmReceptions_Distribution",
  "alarm-codes": "Menu:AlarmReceptions_AlarmCodes",
  "technical-settings": "Menu:AlarmReceptions_TechnicalSettings",
  "reception-integrations": "AlarmReceptions:arс_integration",
  "call-resources-settings": "Menu:AlarmReceptions_CallResourcesSettings",
  contacts: "Menu:AlarmReceptions_Contacts",
  subscriptions: "Menu:AlarmReceptions_Subscriptions",

  // Transmitters detail pages
  transmitters: "Menu:Transmitters",
  logs: "Menu:Transmitter_logs",

  // Cameras detail pages
  cameras: "Menu:Cameras",

  // Locations detail pages
  locations: "Menu:Locations",

  // Room Identifiers (Observation Identifiers) page
  "room-identifiers": "Menu:Observation_identifiers",

  // Common Contacts detail pages
  "common-contacts": "Common:contacts_label",
  availability: "Menu:availability",
  "associated-objects": "Menu:associated_objects",

  // Resellers detail pages
  resellers: "Common:sellers",
  "ip-white-list": "Organizations:settings_ip_white_list",

  // Role templates
  roletemplates: "Common:role_templates",
};

// Context-specific translation mappings: segment -> path pattern -> translation key
const contextSpecificTranslations: Record<
  string,
  Array<{ pathPattern: string; translationKey: string }>
> = {
  "advanced-settings": [
    {
      pathPattern: "/objects/",
      translationKey: "Common:additional_object_information",
    },
    {
      pathPattern: "/organizations/",
      translationKey: "Menu:advanced_settings",
    },
  ],
  settings: [
    {
      pathPattern: "/administrators/",
      translationKey: "Administrator:settings_title",
    },
    { pathPattern: "/organizations/", translationKey: "Menu:settings" },
  ],
  "menu-alternatives": [
    {
      pathPattern: "/administrators/",
      translationKey: "Menu:Menu_Alternatives",
    },
    { pathPattern: "/resellers/", translationKey: "Menu:Menu_Alternatives" },
  ],
  "retention-details": [
    {
      pathPattern: "/organizations/",
      translationKey: "Menu:Organization_RetentionDetails",
    },
    {
      pathPattern: "/resellers/",
      translationKey: "Menu:Organization_RetentionDetails",
    },
  ],
};

// Mapping for detail page translation keys (used when numeric ID is encountered)
const detailPageTranslationMap: Record<string, string> = {
  objects: "Menu:general",
  transmitters: "Menu:Transmitter_details",
  organizations: "Menu:general",
  administrators: "Menu:general",
  resellers: "Menu:general",
  "common-contacts": "Menu:general",
  locations: "Menu:Location_details",
  cameras: "Menu:Camera_details",
  "alarm-receptions": "Menu:general",
  identity: "Menu:general",
};

const getTranslationKey = (
  segment: string,
  path: string,
): string | undefined => {
  const contextMappings = contextSpecificTranslations[segment];
  if (contextMappings) {
    const matchingMapping = contextMappings.find((mapping) =>
      path.includes(mapping.pathPattern),
    );
    if (matchingMapping) {
      return matchingMapping.translationKey;
    }
  }

  return routeTranslationMap[segment];
};

export const usePageTitle = () => {
  const location = useLocation();
  const branding = useBranding();
  const t = useTranslations();
  const user = useUser();
  const menu = useMenu(user);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const baseTitle = branding.currentTitle || "Skyresponse";

    const getSubtitleFromRoute = (): string | undefined => {
      const path = location.pathname;

      // Handle root adminportal route
      if (path === "/adminportal" || path === "/adminportal/") {
        return t("Menu:home");
      }

      const pathSegments = path.split("/");
      const segments = pathSegments
        .map((item, index, array) => {
          if (Number(item)) {
            // For numeric segments (IDs), only include "General" if this is the last segment
            // (i.e., we're on the detail page itself, not a sub-page)
            const hasMoreSegments = array
              .slice(index + 1)
              .some((seg) => seg && seg !== "adminportal");

            // If there are more segments after the ID, skip "General" (we're on a sub-page)
            if (hasMoreSegments) {
              return "";
            }

            // For numeric segments (IDs), find the previous non-numeric segment
            // and use the detail page translation key for that route
            let previousSegment = "";
            for (let i = index - 1; i >= 0; i--) {
              if (
                pathSegments[i] &&
                !Number(pathSegments[i]) &&
                pathSegments[i] !== "adminportal"
              ) {
                previousSegment = pathSegments[i];
                break;
              }
            }

            if (previousSegment) {
              const detailTranslationKey =
                detailPageTranslationMap[previousSegment];
              return detailTranslationKey
                ? t(detailTranslationKey)
                : t("Menu:general");
            }
            return index === array.length - 1 ? t("Menu:general") : "";
          }
          const translationKey = getTranslationKey(item, path);
          return translationKey ? t(translationKey) : item;
        })
        .filter((i) => i && i !== "adminportal")
        .reverse();

      return segments.join(" - ");
    };

    const updateTitle = () => {
      let pageTitle: string | null = null;

      const subtitle = getSubtitleFromRoute();

      let finalTitle = pageTitle ?? "";
      if (subtitle) {
        finalTitle = `${subtitle} ${finalTitle}`;
      }
      const fullTitle = `${finalTitle} - ${baseTitle}`;
      document.title = fullTitle;

      return pageTitle;
    };

    updateTitle();

    const observer = new MutationObserver(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        updateTitle();
      }, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.title = baseTitle;
    };
  }, [location.pathname, branding.currentTitle, menu, t, user]);
};
